/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { getUserTypePreference } from '../../../../util/dataExtractors';
import { UserTypePreference } from '../../../../util/enums';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../../../util/urlHelpers';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isMessagesPage = tab => hasPageName('MessagesPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) ||
      isInboxPage(route?.params?.tab) ||
      isMessagesPage(route?.params?.tab) ||
      isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    const isMessagesPage =
      currentPage?.indexOf('MessagesPage') === 0 && page?.indexOf('MessagesPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage || isMessagesPage
      ? css.currentPage
      : null;
  };

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.accountLinksWrapper}>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
              name="SearchPage"
            >
              Our chefs
            </NamedLink>
            <Link
              to="/p/about"
              className={classNames(css.navigationLink, currentPageClass('CMSPage:about'))}
            >
              <FormattedMessage id="TopbarMobileMenu.aboutUsLink" />
            </Link>
            <NamedLink
              name="SignupForUserTypePage"
              params={{ userType: UserTypePreference.PRIVATE_CHEFS }}
              className={css.navigationLink}
            >
              Chef Register
            </NamedLink>
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  const userType = getUserTypePreference(currentUser);
  const isSeller = userType === UserTypePreference.PRIVATE_CHEFS;
  const authorListingId = currentUser?.attributes?.profile?.publicData?.authorListingId || '';
  const title = currentUser?.attributes?.profile?.displayName;
  const slug = title ? createSlug(title) : 'profile';
  const isChefProfilePublished = currentUser?.attributes?.profile?.publicData?.isPublished;
  const chefProfileLinkProps = authorListingId
    ? {
        name: 'EditListingPage',
        params: {
          id: authorListingId,
          slug,
          type: isChefProfilePublished
            ? LISTING_PAGE_PARAM_TYPE_EDIT
            : LISTING_PAGE_PARAM_TYPE_DRAFT,
          tab: 'basic-info',
        },
      }
    : { name: 'NewListingPage' };

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass(`MessagesPage:${inboxTab}`))}
            name="MessagesPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.messagesLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('WishlistPage'))}
            name="WishlistPage"
          >
            <FormattedMessage id="TopbarMobileMenu.wishlistLink" defaultMessage="Wishlist" />
          </NamedLink>

          {/* {manageListingsLinkMaybe} */}
          {isSeller ? (
            <>
              <NamedLink
                {...chefProfileLinkProps}
                className={classNames(css.navigationLink, currentPageClass('EditListingPage'))}
              >
                <FormattedMessage
                  id={
                    isChefProfilePublished
                      ? 'TopbarMobileMenu.yourChefProfile'
                      : 'TopbarMobileMenu.completeChefProfile'
                  }
                />
              </NamedLink>
            </>
          ) : (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
